import { faCheckCircle, faTriangleExclamation, faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'warn';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  title?: string;
  autoClose?: number | false;
}

interface ToastOptions {
  title?: string;
  autoClose?: number | false;
}

interface ToastContextType {
  showToast: (message: string, type: ToastType, options?: ToastOptions) => void;
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  warn: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = (message: string, type: ToastType, options?: ToastOptions) => {
    const id = Date.now();
    const autoClose = options?.autoClose !== undefined ? options.autoClose : 5000;
    setToasts(prev => [...prev, { id, message, type, title: options?.title, autoClose }]);
    
    if (autoClose !== false) {
      setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
      }, autoClose);
    }
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const value: ToastContextType = {
    showToast,
    success: (message: string, options?: ToastOptions) => showToast(message, 'success', options),
    error: (message: string, options?: ToastOptions) => showToast(message, 'error', options),
    warn: (message: string, options?: ToastOptions) => showToast(message, 'warn', options),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            message={toast.message}
            type={toast.type}
            title={toast.title}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

interface ToastItemProps {
  message: string;
  type: ToastType;
  title?: string;
  onClose: () => void;
}

function ToastItem({ message, type, title, onClose }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const config = {
    success: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-400',
      textColor: 'text-green-800',
      iconColor: 'text-green-600',
      icon: faCheckCircle,
      defaultTitle: 'Success!',
    },
    error: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-400',
      textColor: 'text-red-800',
      iconColor: 'text-red-600',
      icon: faXmarkCircle,
      defaultTitle: 'Error!',
    },
    warn: {
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-400',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-600',
      icon: faTriangleExclamation,
      defaultTitle: 'Warning!',
    },
  };

  const { bgColor, borderColor, textColor, iconColor, icon: Icon, defaultTitle } = config[type];
  const displayTitle = title || defaultTitle;

  return (
    <div
      className={`${bgColor} ${borderColor} ${textColor} border-l-4 p-4 rounded shadow-lg min-w-80 max-w-md transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <div className="flex items-start gap-3">
        <FontAwesomeIcon icon={Icon} className={`${iconColor} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <strong className="font-semibold block mb-1">{displayTitle}</strong>
          <div className="text-sm">{message}</div>
        </div>
        <button
          onClick={onClose}
          className={`${textColor} hover:opacity-70 flex-shrink-0`}
        >
          <FontAwesomeIcon icon={faXmarkCircle}/>
        </button>
      </div>
    </div>
  );
}

export default ToastProvider;

